import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import { ZModal, ZModalBody, ZModalContent, ZModalHeader } from "@/shared/components/modal";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile } from "@/shared/types/file-upload";
import { Loader2 } from 'lucide-react';
import type { FC } from "react";
import { useState } from "react";
import { useRespondOffline } from "../../requests";

interface RespondOfflineModalProps {
  /**
   * Handler called when the user confirms to submit
   */
  onConfirm: (response?: any) => void;

  /**
   * Handler called when the modal is closed without submitting
   */
  onClose: () => void;

  /**
   * Whether the modal is open or not
   */
  isOpen: boolean;

  /**
   * Handler for downloading the questionnaire template
   */
  onDownloadQuestionnaire?: () => void;

  /**
   * Whether the download is in progress
   */
  isDownloading?: boolean;

  /**
   * Handler for file upload
   */
  onFileUpload?: (files: UploadedFile[]) => void;

  /**
   * Token from email link for authentication
   */
  token: string;

  /**
   * URL for the questionnaire Excel template
   */
  questionnaireExcelUrl?: string;
}

const RespondOfflineModal: FC<RespondOfflineModalProps> = ({
  onConfirm,
  onClose,
  isOpen,
  onDownloadQuestionnaire,
  isDownloading = false,
  onFileUpload,
  token,
  questionnaireExcelUrl,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const respondOfflineMutation = useRespondOffline();

  const handleFileChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
    if (onFileUpload) {
      onFileUpload(files);
    }
  };

  const handleDownload = () => {
    if (questionnaireExcelUrl) {
      setIsDownloadLoading(true);
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = questionnaireExcelUrl;
      link.download = 'questionnaire-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Reset loading state after a short delay to ensure download starts
      setTimeout(() => setIsDownloadLoading(false), 1000);
    } else if (onDownloadQuestionnaire) {
      onDownloadQuestionnaire();
    }
  };

  const handleSubmit = async () => {
    const validFile = uploadedFiles.find(file => file.status === "success");

    if (!validFile?.signedUrl) {
      addNotification({
        type: "error",
        title: "Upload Error",
        message: "Please upload a valid file before submitting.",
      });
      return;
    }

    try {
      const response = await respondOfflineMutation.mutateAsync({
        token,
        payload: {
          excel_file_url: validFile.signedUrl,
        },
      });

      // Don't show success toast here - let the parent component handle it
      onConfirm(response);
    } catch (error) {

      addNotification({
        type: "error",
        title: "Import Failed",
        message: "Zania was unable to import the questionnaire. Ensure the format of the file is correct and try again.",
      });
    }
  };

  const hasValidFiles = uploadedFiles.some(file => file.status === "success");
  const hasFileUploadError = uploadedFiles.some(file =>
    ["duplicate_files", "too_large", "too_many_files", "failed"].includes(file.status)
  );

  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={onClose}
    >
      <ZModalContent className="w-[512px] max-w-[512px] bg-white shadow-[0px_0px_24px_rgba(0,0,0,0.05)] rounded-[16px] p-6 pt-2 flex flex-col">
        <ZModalHeader className="flex flex-col w-full items-center pb-0 mb-2">
          <p className="text-[18px] font-semibold text-[#11181C] leading-[28px]">Respond Offline</p>
        </ZModalHeader>
        <ZModalBody className="p-0 mb-8 flex-1">
          <div className="flex flex-col gap-4 h-full">
            {/* Warning message */}
            <div className="flex flex-col items-center">
              <p className="text-[14px] text-[#F31260] text-center leading-[20px] max-w-[464px]">
                Note that all existing responses will be overridden.
              </p>
            </div>

            {/* Step 1: Download Questionnaire */}
            <div className="flex flex-col gap-1.5">
              <h3 className="text-[14px] font-medium text-[#11181C]">
                Step 1: Download Questionnaire
              </h3>
              <p className="text-[14px] text-[#71717A] leading-[20px]">
                Download the questionnaire template to fill in your responses offline.
              </p>
              <ZButton
                variant="bordered"
                className="w-fit mt-1 border-1 text-[14px] font-medium h-[28px] border-[#006FEE] text-[#006FEE] bg-white "
                onClick={handleDownload}
                isDisabled={isDownloading || isDownloadLoading}
              >
                <span>{(isDownloading || isDownloadLoading) ? <Loader2 className="animate-spin text-sm" /> : null}</span>
                Download Questionnaire
              </ZButton>
            </div>

            {/* Step 2: Fill in Responses */}
            <div className="flex flex-col gap-1.5">
              <h3 className="text-[14px] font-medium text-[#11181C]">
                Step 2: Fill in Responses
              </h3>

            </div>

            {/* Step 3: Upload Completed File */}
            <div className="flex flex-col gap-1.5">
              <h3 className="text-[14px] font-medium text-[#11181C]">
                Step 3: Upload Completed File
              </h3>
              <FileUpload
                isMultiple={false}
                maxFiles={1}
                allowedFileTypes={[
                  "text/csv",
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                ]}
                setFiles={handleFileChange}
                maxSizeMB={20}
              />
            </div>
          </div>
        </ZModalBody>

        {/* Footer Container */}
        <div className="flex flex-col gap-4 mt-auto">
          {/* Separator */}
          <div className="w-full h-0 border-t border-[#DCDCDC]" />

          {/* Buttons */}
          <div className="flex justify-end items-center gap-4">
            <ZButton
              variant="bordered"
              className="px-3 py-1 text-[14px] font-medium h-[28px] border-[#006FEE] text-[#006FEE] bg-white"
              onClick={onClose}
              isDisabled={respondOfflineMutation.isPending}
            >
              Cancel
            </ZButton>
            <ZButton
              className="px-3 py-1 text-[14px] font-medium h-[28px] bg-[#006FEE] text-white border-none rounded-[6px] hover:bg-[#0056CC]"
              onClick={handleSubmit}
              isDisabled={!hasValidFiles || hasFileUploadError || respondOfflineMutation.isPending}
              isLoading={respondOfflineMutation.isPending}
            >
              Submit
            </ZButton>
          </div>
        </div>
      </ZModalContent>
    </ZModal>
  );
};

export default RespondOfflineModal;
